import { DayObject, DayRange } from "@/types/date"
import { ItineraryEvent } from "@/types/itinerary"
import { Place } from "@/types/place"
import { TravelerGroup } from "@/types/trip"

import {
  getNextAvailableReservationDate,
  getReservationTimeOptions,
} from "@/lib/business-hours-utils"
import {
  afterDayObject,
  beforeDayObject,
  browserToday,
  dayAfter,
  today,
} from "@/lib/date-utils"
import {
  isAirport,
  isHotel,
  isLocation,
  isRestaurant,
} from "@/lib/entity-utils"
import {
  isPlaceSearchDatesFresh,
  PlaceSearchDateBucket,
} from "@/lib/place-search-memory"
import { PlaceDateKind } from "@/lib/place-utils"
import { EntitySheetPlaceSearchParams } from "@/lib/storage/detail-sheet-slice"
import { defaultTravelers } from "@/lib/trip-utils"

export type { PlaceSearchDateBucket }

export interface PlaceSearchPreference {
  travelers?: TravelerGroup
  room?: import("@/types/trip").Room
  stay?: PlaceSearchDateBucket
  day?: PlaceSearchDateBucket
}

export interface PreferredPlaceSearchParams {
  travelers: TravelerGroup
  startDate: DayObject | undefined
  endDate: DayObject | undefined
}

function getPreferenceDatesForKind(
  preference: PlaceSearchPreference | undefined,
  dateKind: PlaceDateKind,
  browserTodayParam: DayObject
): { startDate: DayObject; endDate: DayObject } | undefined {
  const bucket = dateKind === "stay" ? preference?.stay : preference?.day
  if (!bucket || !isPlaceSearchDatesFresh(bucket, browserTodayParam)) {
    return undefined
  }
  return { startDate: bucket.startDate, endDate: bucket.endDate }
}

function tripDatesAreValid(
  tripDates: DayRange,
  browserTodayParam: DayObject
): boolean {
  return (
    tripDates.from !== undefined &&
    !beforeDayObject(tripDates.from, browserTodayParam)
  )
}

/**
 * Preference-layer resolver for map pins and price queries (no Place required).
 * Session pick beats trip; trip beats remembered localStorage.
 */
export function resolvePreferredPlaceSearchParams(input: {
  dateKind: PlaceDateKind
  browserToday: DayObject
  preference: PlaceSearchPreference | undefined
  tripDates: DayRange
  tripTravelers: TravelerGroup | undefined
  rememberedTravelers: TravelerGroup | undefined
  rememberedDates: { startDate: DayObject; endDate: DayObject } | undefined
}): PreferredPlaceSearchParams {
  const {
    dateKind,
    browserToday: browserTodayParam,
    preference,
    tripDates,
    tripTravelers,
    rememberedTravelers,
    rememberedDates,
  } = input

  const travelers =
    preference?.travelers ||
    tripTravelers ||
    rememberedTravelers ||
    defaultTravelers()

  const sessionDates = getPreferenceDatesForKind(
    preference,
    dateKind,
    browserTodayParam
  )

  let startDate: DayObject | undefined
  let endDate: DayObject | undefined

  if (sessionDates) {
    startDate = sessionDates.startDate
    endDate = sessionDates.endDate
  } else if (tripDatesAreValid(tripDates, browserTodayParam)) {
    startDate = tripDates.from
    endDate = tripDates.to
  } else if (rememberedDates) {
    startDate = rememberedDates.startDate
    endDate = rememberedDates.endDate
  }

  return { travelers, startDate, endDate }
}

function getRestaurantReservationDate(
  place: Place,
  fromDate: DayObject,
  untilDate?: DayObject
): DayObject {
  if (!isRestaurant(place) || !place.detailed || !place.business_hours) {
    return fromDate
  }

  return getNextAvailableReservationDate({
    hours: place.business_hours,
    timeZone: place.time_zone,
    fromDate,
    untilDate,
  })
}

function getRestaurantReservationDates(
  place: Place,
  fromDate: DayObject,
  untilDate?: DayObject
): { startDate: DayObject; endDate: DayObject } {
  const startDate = getRestaurantReservationDate(place, fromDate, untilDate)
  return { startDate, endDate: startDate }
}

export function restaurantDateHasSlots(place: Place, date: DayObject): boolean {
  if (!isRestaurant(place) || !place.detailed || !place.business_hours) {
    return true
  }

  return (
    getReservationTimeOptions({
      hours: place.business_hours,
      timeZone: place.time_zone,
      date,
    }).length > 0
  )
}

export function getDraftFallbackDates(
  place: Place,
  todayAtPlace: DayObject
): { startDate: DayObject | undefined; endDate: DayObject | undefined } {
  if (isRestaurant(place)) {
    return getRestaurantReservationDates(place, todayAtPlace)
  }

  return { startDate: undefined, endDate: undefined }
}

export function getTripBasedPlaceSearchDates(
  place: Place,
  tripDates: DayRange
): { startDate: DayObject | undefined; endDate: DayObject | undefined } {
  const todayAtPlace = today(place.time_zone)
  let tripStart: DayObject | undefined = tripDates.from
  let tripEnd: DayObject | undefined

  if (isHotel(place) || isLocation(place) || isAirport(place)) {
    const isMidTrip =
      tripStart &&
      tripDates.to &&
      afterDayObject(todayAtPlace, tripStart) &&
      afterDayObject(tripDates.to, todayAtPlace)

    tripStart = isMidTrip ? todayAtPlace : tripStart
    tripEnd = tripDates.to
  } else {
    const isMidTrip =
      tripStart &&
      tripDates.to &&
      afterDayObject(todayAtPlace, tripStart) &&
      !afterDayObject(todayAtPlace, tripDates.to)

    tripStart = tripEnd = isMidTrip ? todayAtPlace : tripStart
  }

  if (tripStart) {
    if (beforeDayObject(tripStart, browserToday())) {
      return getDraftFallbackDates(place, todayAtPlace)
    }

    if (isRestaurant(place)) {
      return getRestaurantReservationDates(place, tripStart, tripDates.to)
    }

    return {
      startDate: tripStart,
      endDate: tripEnd || dayAfter(tripStart),
    }
  }

  return getDraftFallbackDates(place, todayAtPlace)
}

function pickSessionOrIntentDates(input: {
  dateKind: PlaceDateKind
  browserTodayParam: DayObject
  preference: PlaceSearchPreference | undefined
  intentStartDate: DayObject | undefined
  intentEndDate: DayObject | undefined
  messageTimestamp: number | undefined
}): {
  startDate: DayObject | undefined
  endDate: DayObject | undefined
  bumpIfNoSlots: boolean
  normalizeHotelStayRange: boolean
} {
  const sessionDates = getPreferenceDatesForKind(
    input.preference,
    input.dateKind,
    input.browserTodayParam
  )
  const sessionBucket =
    input.dateKind === "stay" ? input.preference?.stay : input.preference?.day

  const hasIntent = input.intentStartDate !== undefined
  const useIntent =
    hasIntent &&
    (!sessionDates ||
      (input.messageTimestamp !== undefined &&
        sessionBucket !== undefined &&
        input.messageTimestamp > sessionBucket.updatedAt))

  if (useIntent && input.intentStartDate) {
    return {
      startDate: input.intentStartDate,
      endDate: input.intentEndDate ?? input.intentStartDate,
      bumpIfNoSlots: false,
      normalizeHotelStayRange: false,
    }
  }

  if (sessionDates) {
    return {
      startDate: sessionDates.startDate,
      endDate: sessionDates.endDate,
      bumpIfNoSlots: true,
      normalizeHotelStayRange: true,
    }
  }

  return {
    startDate: undefined,
    endDate: undefined,
    bumpIfNoSlots: false,
    normalizeHotelStayRange: false,
  }
}

export interface PlaceDetailSearchInput {
  place: Place
  dateKind: PlaceDateKind
  browserToday: DayObject
  tripDates: DayRange
  tripTravelers: TravelerGroup | undefined
  preference: PlaceSearchPreference | undefined
  rememberedTravelers: TravelerGroup | undefined
  rememberedDates: { startDate: DayObject; endDate: DayObject } | undefined
  explicitEventDates?: {
    startDate: DayObject | undefined
    endDate: DayObject | undefined
    event: ItineraryEvent
  }
  confirmedHotelEventDates?: {
    startDate: DayObject | undefined
    endDate: DayObject | undefined
    event: ItineraryEvent
  }
  quoteDates?: {
    startDate: DayObject
    endDate: DayObject
    travelers: TravelerGroup
  }
  sheetParams?: EntitySheetPlaceSearchParams
  autoMatchedEventDates?: {
    startDate: DayObject | undefined
    endDate: DayObject | undefined
    event: ItineraryEvent
  }
  intentStartDate?: DayObject
  intentEndDate?: DayObject
  messageTimestamp?: number
  intentTravelers?: TravelerGroup
}

export interface PlaceDetailSearchResult {
  startDate: DayObject | undefined
  endDate: DayObject | undefined
  travelers: TravelerGroup
  datesSourceEvent: ItineraryEvent | undefined
}

/**
 * Full resolver for place detail init: per-open context, then preference, then defaults.
 */
export function resolvePlaceDetailSearchParams(
  input: PlaceDetailSearchInput
): PlaceDetailSearchResult {
  const { place, dateKind, tripDates } = input

  let newStart: DayObject | undefined
  let newEnd: DayObject | undefined
  let bumpIfNoSlots = false
  let normalizeHotelStayRange = false
  let datesSourceEvent: ItineraryEvent | undefined

  if (input.explicitEventDates) {
    newStart = input.explicitEventDates.startDate
    newEnd = input.explicitEventDates.endDate
    datesSourceEvent = input.explicitEventDates.event
  } else if (input.confirmedHotelEventDates) {
    newStart = input.confirmedHotelEventDates.startDate
    newEnd = input.confirmedHotelEventDates.endDate
    datesSourceEvent = input.confirmedHotelEventDates.event
  } else if (input.quoteDates) {
    newStart = input.quoteDates.startDate
    newEnd = input.quoteDates.endDate
  } else if (input.sheetParams && input.sheetParams.dateKind === dateKind) {
    newStart = input.sheetParams.startDate
    newEnd = input.sheetParams.endDate
    bumpIfNoSlots = true
    normalizeHotelStayRange = true
  } else {
    const sessionOrIntent = pickSessionOrIntentDates({
      dateKind,
      browserTodayParam: input.browserToday,
      preference: input.preference,
      intentStartDate: input.intentStartDate,
      intentEndDate: input.intentEndDate,
      messageTimestamp: input.messageTimestamp,
    })

    if (sessionOrIntent.startDate && sessionOrIntent.endDate) {
      newStart = sessionOrIntent.startDate
      newEnd = sessionOrIntent.endDate
      bumpIfNoSlots = sessionOrIntent.bumpIfNoSlots
      normalizeHotelStayRange = sessionOrIntent.normalizeHotelStayRange
    } else if (input.autoMatchedEventDates) {
      newStart = input.autoMatchedEventDates.startDate
      newEnd = input.autoMatchedEventDates.endDate
      datesSourceEvent = input.autoMatchedEventDates.event
    } else if (input.intentStartDate) {
      newStart = input.intentStartDate
      newEnd = input.intentEndDate ?? input.intentStartDate
    } else if (!tripDates.from) {
      if (input.rememberedDates) {
        newStart = input.rememberedDates.startDate
        newEnd = input.rememberedDates.endDate
        bumpIfNoSlots = true
        normalizeHotelStayRange = true
      } else {
        const tripBasedDates = getTripBasedPlaceSearchDates(place, tripDates)
        newStart = tripBasedDates.startDate
        newEnd = tripBasedDates.endDate
      }
    } else {
      const tripBasedDates = getTripBasedPlaceSearchDates(place, tripDates)
      newStart = tripBasedDates.startDate
      newEnd = tripBasedDates.endDate
    }
  }

  if (
    bumpIfNoSlots &&
    newStart &&
    isRestaurant(place) &&
    !restaurantDateHasSlots(place, newStart)
  ) {
    const reservationDate = getRestaurantReservationDate(place, newStart)
    newStart = reservationDate
    newEnd = reservationDate
  }

  if (
    normalizeHotelStayRange &&
    isHotel(place) &&
    newStart &&
    newEnd &&
    newStart.year === newEnd.year &&
    newStart.month === newEnd.month &&
    newStart.day === newEnd.day
  ) {
    newEnd = dayAfter(newStart)
  }

  const rememberedTravelers = input.rememberedTravelers
  const newTravelers =
    datesSourceEvent?.travelers ||
    input.quoteDates?.travelers ||
    input.sheetParams?.travelers ||
    input.preference?.travelers ||
    input.intentTravelers ||
    input.tripTravelers ||
    rememberedTravelers ||
    defaultTravelers()

  return {
    startDate: newStart,
    endDate: newEnd,
    travelers: newTravelers,
    datesSourceEvent,
  }
}
